/** Client helpers for named application PDF output packs (mirrors server applicationFormPdfPacks). */

export const DEFAULT_PDF_PACK_KEY = 'base';

export function createDefaultPdfOutputs() {
  return [
    {
      key: DEFAULT_PDF_PACK_KEY,
      title: 'Application form',
      order: 0,
      includeSectionKeys: [],
      includePricing: true,
      includeHeaderMeta: true,
      isDefault: true,
      clientId: DEFAULT_PDF_PACK_KEY,
    },
  ];
}

export function createSeparatePdfPack(overrides = {}) {
  const pack = {
    key: 'internal_declaration',
    title: 'Internal declaration',
    order: 1,
    includeSectionKeys: [],
    includePricing: false,
    includeHeaderMeta: true,
    isDefault: false,
    ...overrides,
  };
  return {
    ...pack,
    clientId: pack.clientId || pack.key || `pdf_${Date.now()}`,
  };
}

/** Soft cap so a form does not accidentally mint dozens of packs. */
export const MAX_PDF_OUTPUTS = 20;

export function slugifyPdfPackKey(title, fallback = 'pack') {
  const slug = String(title || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 48);
  return slug || fallback;
}

/** Unique pack key derived from a display title. */
export function allocateUniquePdfPackKey(title, existingKeys = []) {
  const used = new Set((existingKeys || []).map((k) => String(k || '').trim()).filter(Boolean));
  const baseKey = slugifyPdfPackKey(title, 'pack');
  let key = baseKey === DEFAULT_PDF_PACK_KEY ? `${baseKey}_extra` : baseKey;
  let n = 2;
  while (used.has(key)) {
    key = `${baseKey}_${n}`;
    n += 1;
  }
  return key;
}

/**
 * Create an additional named PDF pack with a unique key derived from the title.
 * @param {string} title
 * @param {string[]} existingKeys
 * @param {number} [order]
 */
export function createPdfPackFromTitle(title, existingKeys = [], order = 1) {
  const key = allocateUniquePdfPackKey(title, existingKeys);
  const cleanTitle = String(title || '').trim() || key.replace(/_/g, ' ');
  return {
    key,
    title: cleanTitle,
    order: Number.isFinite(Number(order)) ? Number(order) : (existingKeys || []).length,
    includeSectionKeys: [],
    includePricing: false,
    includeHeaderMeta: true,
    isDefault: false,
    clientId: `pdf_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
  };
}

export function resolvePdfOutputs(settings) {
  const raw = Array.isArray(settings?.pdfOutputs) ? settings.pdfOutputs : [];
  if (!raw.length) return createDefaultPdfOutputs();
  return raw
    .map((p, idx) => {
      const key = String(p?.key || '').trim() || `pack_${idx + 1}`;
      return {
        key,
        title: String(p?.title || '').trim() || (key === DEFAULT_PDF_PACK_KEY ? 'Application form' : key),
        order: Number.isFinite(Number(p?.order)) ? Number(p.order) : idx,
        includeSectionKeys: Array.isArray(p?.includeSectionKeys) ? p.includeSectionKeys : [],
        includePricing: p?.includePricing !== false,
        includeHeaderMeta: p?.includeHeaderMeta !== false,
        isDefault: Boolean(p?.isDefault) || key === DEFAULT_PDF_PACK_KEY,
        // Stable UI id so renaming the auto key does not remount the title input.
        clientId: String(p?.clientId || key),
      };
    })
    .sort((a, b) => a.order - b.order);
}

export function multiPdfEnabled(settings) {
  return Array.isArray(settings?.pdfOutputs) && settings.pdfOutputs.length > 1;
}

export function normalizePdfOutputsForSave(outputs, enabled) {
  if (!enabled) return [];
  const list = Array.isArray(outputs) ? outputs : [];
  if (!list.length) return createDefaultPdfOutputs();
  const seen = new Set();
  const out = [];
  list.forEach((p, idx) => {
    const key = String(p?.key || '').trim().replace(/[^A-Za-z0-9_-]/g, '_') || `pack_${idx + 1}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({
      key,
      title: String(p?.title || '').trim() || (key === DEFAULT_PDF_PACK_KEY ? 'Application form' : key),
      order: idx,
      includeSectionKeys: [],
      includePricing: p?.includePricing !== false,
      includeHeaderMeta: p?.includeHeaderMeta !== false,
      // Signed upload is always available voluntarily — not a form-builder setting.
      requiresSignedUpload: true,
      isDefault: Boolean(p?.isDefault) || key === DEFAULT_PDF_PACK_KEY,
    });
  });
  if (!out.some((p) => p.key === DEFAULT_PDF_PACK_KEY)) {
    out.unshift({ ...createDefaultPdfOutputs()[0], requiresSignedUpload: true });
  }
  let foundDefault = false;
  return out.map((p) => {
    if ((p.isDefault || p.key === DEFAULT_PDF_PACK_KEY) && !foundDefault) {
      foundDefault = true;
      return { ...p, isDefault: true, requiresSignedUpload: true };
    }
    return { ...p, isDefault: false, requiresSignedUpload: true };
  });
}

export function packTitleForKey(settingsOrOutputs, packKey) {
  const packs = Array.isArray(settingsOrOutputs)
    ? settingsOrOutputs
    : resolvePdfOutputs(settingsOrOutputs);
  const key = String(packKey || DEFAULT_PDF_PACK_KEY).trim() || DEFAULT_PDF_PACK_KEY;
  const hit = packs.find((p) => p.key === key);
  return hit?.title || (key === DEFAULT_PDF_PACK_KEY ? 'Application form' : key);
}
